import { Alert, Button, EmptyState, useToast } from '@catering/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Upload } from 'lucide-react';
import { type ChangeEvent, type DragEvent, type JSX, useRef, useState } from 'react';

import {
  confirmDocumentUpload,
  requestDocumentDownloadUrl,
  requestDocumentUploadUrl,
  type ContractDetail,
} from '../../../contracts/api';
import { mapDocumentError } from '../../../contracts/contract-api-errors';

const REQUIRED_CONTENT_TYPE = 'application/pdf';
const MAX_SIZE_BYTES = 5_242_880;

export interface ContractDocumentTabProps {
  contract: ContractDetail;
}

/**
 * `contract-documents/spec.md` → "Validación de tipo mostrada antes de
 * subir", "Reemplazo exitoso de documento": the input stays enabled even
 * when a document already exists — replacing is the same flow as a first
 * upload, not a separate gated action. The `PUT` to the presigned URL
 * (`design.md` §7.4 step 2) goes straight to MinIO/S3; the API never sees
 * the binary.
 */
export function ContractDocumentTab({ contract }: ContractDocumentTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [clientError, setClientError] = useState<string | undefined>(undefined);
  const [progress, setProgress] = useState<number | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const { uploadUrl, key } = await requestDocumentUploadUrl(contract.id, file.type, file.size);
      await uploadToPresignedUrl(uploadUrl, file, setProgress);
      return confirmDocumentUpload(contract.id, key);
    },
    onSuccess: async () => {
      setProgress(null);
      await queryClient.invalidateQueries({ queryKey: ['contract', contract.id] });
      toast('success', 'Documento cargado');
    },
    onError: () => setProgress(null),
  });

  const downloadMutation = useMutation({
    mutationFn: () => requestDocumentDownloadUrl(contract.id),
    onSuccess: (result) => {
      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
    },
  });

  function validateAndUpload(file: File): void {
    setClientError(undefined);

    if (file.type !== REQUIRED_CONTENT_TYPE) {
      setClientError('El archivo debe ser un PDF.');
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      setClientError('El archivo supera el tamaño máximo permitido (5 MB).');
      return;
    }

    setProgress(0);
    uploadMutation.mutate(file);
  }

  function handleFileInputChange(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (file !== undefined) {
      validateAndUpload(file);
    }
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setIsDraggingOver(false);
    const file = event.dataTransfer.files[0];

    if (file !== undefined) {
      validateAndUpload(file);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {clientError !== undefined ? <Alert tone="danger">{clientError}</Alert> : null}
      {uploadMutation.isError ? (
        <Alert tone="danger">{mapDocumentError(uploadMutation.error)}</Alert>
      ) : null}
      {downloadMutation.isError ? (
        <Alert tone="danger">No pudimos generar el enlace de descarga.</Alert>
      ) : null}

      <div
        onDragOver={(event) => {
          event.preventDefault();
          setIsDraggingOver(true);
        }}
        onDragLeave={() => setIsDraggingOver(false)}
        onDrop={handleDrop}
        className={`flex flex-col items-center gap-3 rounded-md border-2 border-dashed p-8 text-center ${
          isDraggingOver ? 'border-slate-900 bg-slate-50' : 'border-slate-300'
        }`}
      >
        <Upload aria-hidden="true" className="size-6 text-slate-400" />
        <p className="text-sm text-slate-600">Arrastrá y soltá el PDF del contrato acá, o</p>
        <label className="cursor-pointer text-sm font-medium text-slate-900 underline">
          Seleccioná un archivo
          <input ref={inputRef} type="file" accept="application/pdf" className="sr-only" onChange={handleFileInputChange} />
        </label>
        <p className="text-xs text-slate-500">Solo PDF, hasta 5 MB.</p>
      </div>

      {progress !== null ? (
        <div className="flex flex-col gap-1">
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
            <div className="h-full bg-slate-900 transition-all" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-slate-500">Subiendo… {progress}%</p>
        </div>
      ) : null}

      {contract.documentUrl !== null ? (
        <Button
          variant="secondary"
          icon={Download}
          loading={downloadMutation.isPending}
          onClick={() => downloadMutation.mutate()}
        >
          Descargar documento
        </Button>
      ) : (
        <EmptyState icon={FileText} title="Todavía no hay un documento cargado." />
      )}
    </div>
  );
}

/** Direct `PUT` to MinIO/S3 with upload progress; `XMLHttpRequest` rather than `fetch`, since `fetch` exposes no upload progress event. */
function uploadToPresignedUrl(
  uploadUrl: string,
  file: File,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}
